import type { RunDetail, RunSummary } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '../../lib/api';
import { dateTime, humanize } from '../../lib/format';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, PageTitle } from '../../ui';
import { TaskCard } from './TaskCard';

const STATUS_TONE = { aperta: 'blue', chiusa: 'green', annullata: 'gray' } as const;

export function RunRow({ run }: { run: RunSummary }) {
  const { tenant } = useTenant();
  const pct = run.tasks_total ? Math.round((run.tasks_closed / run.tasks_total) * 100) : 0;
  return (
    <button className="flex w-full flex-wrap items-center gap-3 py-3 text-left hover:bg-black/[0.02]" onClick={() => navigate(`/t/${tenant.id}/esecuzioni/${run.id}`)}>
      <div className="min-w-0 flex-1">
        <p className="font-medium">{run.process_name}</p>
        <p className="text-xs text-ink/55">
          {run.site_name && <>sede {run.site_name} · </>}avviata {dateTime(run.started_at)}
          {run.closed_at && <> · chiusa {dateTime(run.closed_at)}</>}
        </p>
      </div>
      <div className="w-32">
        <div className="h-1.5 rounded-full bg-black/10">
          <div className="h-1.5 rounded-full bg-accent" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-1 text-right text-xs text-ink/55">
          {run.tasks_closed}/{run.tasks_total} task
        </p>
      </div>
      <Badge tone={STATUS_TONE[run.status]}>{run.status}</Badge>
    </button>
  );
}

export function Runs() {
  const runs = useTenantQuery<{ data: RunSummary[] }>(['runs'], '/runs', { refetchInterval: 15_000 });
  return (
    <>
      <PageTitle title="Esecuzioni" subtitle="Ogni avvio di un processo, con l'avanzamento dei suoi task." />
      <ErrorText>{runs.error ? errorMessage(runs.error) : ''}</ErrorText>
      {runs.data?.data.length === 0 && <Empty>Nessuna esecuzione: avviane una dalla pagina di un processo.</Empty>}
      {!!runs.data?.data.length && (
        <Card className="py-2">
          <div className="divide-y divide-black/5">
            {runs.data.data.map((r) => (
              <RunRow key={r.id} run={r} />
            ))}
          </div>
        </Card>
      )}
    </>
  );
}

function InfoValue({ value }: { value: unknown }) {
  if (Array.isArray(value)) {
    if (value.every((v) => v && typeof v === 'object' && 'person_id' in v)) return <span>{value.filter((v: any) => v.present).length} presenti su {value.length}</span>;
    return (
      <ul className="list-inside list-disc">
        {value.map((v, i) => (
          <li key={i}>{v && typeof v === 'object' ? Object.values(v).join(' · ') : String(v)}</li>
        ))}
      </ul>
    );
  }
  return <span>{String(value)}</span>;
}

export function RunDetailPage({ runId }: { runId: string }) {
  const { tenant, t, isManager } = useTenant();
  const queryClient = useQueryClient();
  const run = useTenantQuery<RunDetail>(['runs', runId], `/runs/${runId}`, { refetchInterval: 10_000 });
  const cancel = useMutation({ mutationFn: () => api<RunDetail>(t(`/runs/${runId}/cancel`), { method: 'POST' }), onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] }) });
  if (!run.data) return <ErrorText>{run.error ? errorMessage(run.error) : ''}</ErrorText>;
  const r = run.data;
  const open = r.tasks.filter((x) => x.status === 'aperta');
  const done = r.tasks.filter((x) => x.status !== 'aperta');
  return (
    <>
      <PageTitle
        title={r.process_name}
        subtitle={`Avviata ${dateTime(r.started_at)} · ${r.tasks_closed}/${r.tasks_total} task chiusi`}
        actions={
          <div className="flex items-center gap-2">
            <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge>
            {isManager && r.status === 'aperta' && (
              <Button variant="ghost" onClick={() => confirm('Annullare l’esecuzione e tutti i suoi task aperti?') && cancel.mutate()}>
                Annulla esecuzione
              </Button>
            )}
          </div>
        }
      />
      <ErrorText>{cancel.error ? errorMessage(cancel.error) : ''}</ErrorText>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_18rem]">
        <div className="space-y-6">
          <section>
            <h2 className="mb-2 text-sm font-semibold">Da fare ({open.length})</h2>
            {open.length ? <div className="grid gap-3">{open.map((task) => <TaskCard key={task.id} task={task} showProcess={false} />)}</div> : <Empty>Nessun task aperto.</Empty>}
          </section>
          {!!done.length && (
            <section>
              <h2 className="mb-2 text-sm font-semibold">Fatti ({done.length})</h2>
              <div className="grid gap-2">{done.map((task) => <TaskCard key={task.id} task={task} showProcess={false} />)}</div>
            </section>
          )}
        </div>
        <Card className="h-fit">
          <h2 className="mb-3 text-sm font-semibold">Informazioni raccolte</h2>
          <dl className="space-y-3 text-sm">
            {r.information.map((i) => (
              <div key={i.key}>
                <dt className="font-medium">{humanize(i.key)}</dt>
                <dd className="text-ink/75">
                  <InfoValue value={i.value} />
                </dd>
                <dd className="text-xs text-ink/45">
                  {humanize(i.acquisition_mode)} · {i.source.startsWith('documento:') ? 'da file caricato (in Archivio)' : i.source} · {dateTime(i.acquired_at)}
                </dd>
              </div>
            ))}
          </dl>
        </Card>
      </div>
    </>
  );
}
